import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity"
import { AwsClient } from "aws4fetch"
import awsmobile from "src/aws-exports"


/**
 * IdP discovery for FedRAMP multi-IdP support.
 *
 * The discovery endpoint is called BEFORE the user is authenticated, so the
 * authenticated GraphQL client (which depends on an OIDC id_token) cannot be
 * reused. AppSync allows this field via guest IAM, so we use Cognito Identity
 * Pool unauthenticated credentials and SigV4-sign the request.
 *
 * Backend contract: /docs/idp-discovery-graphql-process.md in
 * denali-digital-backend.
 */

const GRAPHQL_ENDPOINT =
  import.meta.env.VITE_GRAPHQL_ENDPOINT || awsmobile.aws_appsync_graphqlEndpoint
const GRAPHQL_REGION = awsmobile.aws_appsync_region
const COGNITO_IDENTITY_POOL_ID = awsmobile.aws_cognito_identity_pool_id

export type IdpDiscoveryResult = {
  result: "ROUTE_AVAILABLE" | "ROUTE_UNAVAILABLE"
  idpProviderName: string | null
  matchType: "DOMAIN" | "DEFAULT" | "NONE"
  normalizedDomain?: string
  usedDefault?: boolean
}

const DISCOVER_IDENTITY_PROVIDER = `
  query DiscoverIdentityProvider($email: String!) {
    discoverIdentityProvider(email: $email)
  }
`

// Allowlist for the provider name we will forward to Cognito's
// /oauth2/authorize. Prevents a compromised or misconfigured backend from
// injecting arbitrary query parameters into the auth URL.
const PROVIDER_NAME_PATTERN = /^[A-Za-z0-9_.-]{1,64}$/

// Exported so callers that persist/re-read a provider name outside of this
// module (e.g. via sessionStorage across a page reload) can re-validate it
// before use instead of trusting the stored value as-is.
export const isValidProviderName = (name: string): boolean =>
  PROVIDER_NAME_PATTERN.test(name)

const UNAVAILABLE: IdpDiscoveryResult = {
  result: "ROUTE_UNAVAILABLE",
  idpProviderName: null,
  matchType: "NONE",
}

type DiscoverIdentityProviderResponse = {
  data?: {
    discoverIdentityProvider?: string | IdpDiscoveryResult | null
  }
  errors?: Array<{
    message?: string
  }>
}

const credentialsProvider = fromCognitoIdentityPool({
  clientConfig: { region: GRAPHQL_REGION },
  identityPoolId: COGNITO_IDENTITY_POOL_ID,
})

const getClient = async () => {
  const credentials = await credentialsProvider()

  return new AwsClient({
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey,
    sessionToken: credentials.sessionToken,
    region: GRAPHQL_REGION,
    service: "appsync",
  })
}

/**
 * Calls the IdP discovery resolver. Always resolves — any network or parsing
 * error is mapped to ROUTE_UNAVAILABLE so callers can fall back silently to
 * the native Cognito login flow (the fallback is intentional: it avoids
 * leaking whether a given email domain is registered).
 */
export const discoverIdentityProvider = async (
  email: string,
): Promise<IdpDiscoveryResult> => {
  try {
    if (!GRAPHQL_ENDPOINT || !GRAPHQL_REGION || !COGNITO_IDENTITY_POOL_ID) {
      console.error("IdP discovery is missing AppSync or Identity Pool config")
      return UNAVAILABLE
    }

    const response = await (await getClient()).fetch(GRAPHQL_ENDPOINT, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        query: DISCOVER_IDENTITY_PROVIDER,
        variables: { email },
      }),
    })

    const payload = (await response.json()) as DiscoverIdentityProviderResponse

    if (!response.ok || payload.errors?.length) {
      console.error("IdP discovery failed:", payload.errors || response.status)
      return UNAVAILABLE
    }

    const raw = payload.data?.discoverIdentityProvider

    if (!raw) {
      return UNAVAILABLE
    }

    const parsed: IdpDiscoveryResult =
      typeof raw === "string" ? JSON.parse(raw) : raw

    if (
      parsed?.result === "ROUTE_AVAILABLE" &&
      parsed.idpProviderName &&
      PROVIDER_NAME_PATTERN.test(parsed.idpProviderName)
    ) {
      return parsed
    }

    return UNAVAILABLE
  } catch (error) {
    console.error("IdP discovery failed:", error)
    return UNAVAILABLE
  }
}
